import React, { useEffect, useState } from 'react';
import { DailySummary } from './DailySummary';
import { Activity, BarChart2, CheckCircle2, Circle, Lightbulb, RefreshCw, ArrowRight, BookOpen, Target, HeartPulse, Moon, PlusCircle } from 'lucide-react';
import { healthAPI } from '../api';
import { HomeContext } from '../types';
import { SleepForm } from './SleepForm';
import { WaterProgressBar } from './WaterProgressBar';
import './HomePage.css';

interface HomePageProps {
    onNavigate: (view: any) => void;
    refreshTrigger: number;
}

export const HomePage: React.FC<HomePageProps> = ({ onNavigate, refreshTrigger }) => {
    const [context, setContext] = useState<HomeContext | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionComplete, setActionComplete] = useState<'done' | 'decline' | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [syncSuccess, setSyncSuccess] = useState(false);

    const loadContext = async (force: boolean = false) => {
        if (!force) setLoading(true);
        try {
            const data = await healthAPI.getHomeContext(force);
            setContext(data);
            setSyncError(null);
        } catch (err: any) {
            console.error('Failed to load home context:', err);
            if (err.message && err.message.includes('Rate limit')) {
                setSyncError(err.message);
            }
        } finally {
            setLoading(false);
            setActionLoading(false);
            setSyncLoading(false);
        }
    };

    const handleManualSync = async () => {
        setSyncLoading(true);
        setSyncError(null);
        setSyncSuccess(false);

        try {
            await loadContext(true);
            setSyncSuccess(true);
            setTimeout(() => setSyncSuccess(false), 3000);
        } catch (err) {
            console.error('Sync failed:', err);
        }
    };

    useEffect(() => {
        loadContext();
    }, [refreshTrigger]);

    const handleAction = async (action: 'done' | 'decline') => {
        if (!context?.next_action_suggestion) return;
        setActionLoading(true);

        try {
            if (action === 'done') {
                await healthAPI.createEvent({
                    type: 'task_completion',
                    data: {
                        task_description: context.next_action_suggestion,
                        completed_at: new Date().toISOString()
                    }
                });
            }
            // Refresh with force to get a new suggestion
            await loadContext(true);

            // Show acknowledgment
            setActionComplete(action);
            setTimeout(() => setActionComplete(null), 3000);
        } catch (e) {
            console.error(`Failed to process action ${action}`, e);
            setActionLoading(false);
        }
    };

    const getHealthScoreColor = (status?: 'green' | 'yellow' | 'red') => {
        switch (status) {
            case 'green': return 'var(--success-color, #22c55e)';
            case 'yellow': return 'var(--warning-color, #eab308)';
            case 'red': return 'var(--error-color, #ef4444)';
            default: return 'var(--text-secondary)';
        }
    };

    return (
        <div className="home-page">
            {loading ? (
                <div className="context-loading">
                    <RefreshCw className="spin" />
                    <span>Analyzing daily focus...</span>
                </div>
            ) : context && (
                <div className="ai-context-section">

                    {/* Sleep Prompt - Only show if not logged today */}
                    {(!context.is_sleep_logged) && (
                        <SleepForm
                            onSuccess={() => window.location.reload()}
                            onCancel={() => { }}
                        />
                    )}

                    {/* Water Progress Bar - Only show if water tracking enabled */}
                    {(context.water_total_ml !== undefined && context.water_goal_ml !== undefined) && (
                        <WaterProgressBar
                            currentMl={context.water_total_ml}
                            goalMl={context.water_goal_ml}
                        />
                    )}

                    {/* Macro Summary Row */}
                    {context.macros && (
                        <div className="home-macro-summary">
                            <div className="macro-item calories">
                                <span className="macro-value">{Math.round(context.macros.calories)}</span>
                                <span className="macro-label">Cal</span>
                            </div>
                            <div className="macro-item protein">
                                <span className="macro-value">{Math.round(context.macros.protein)}g</span>
                                <span className="macro-label">Prot</span>
                            </div>
                            <div className="macro-item carbs">
                                <span className="macro-value">{Math.round(context.macros.carbs)}g</span>
                                <span className="macro-label">Carbs</span>
                            </div>
                            <div className="macro-item fat">
                                <span className="macro-value">{Math.round(context.macros.fat)}g</span>
                                <span className="macro-label">Fat</span>
                            </div>
                            <div className="macro-item fiber">
                                <span className="macro-value">{Math.round(context.macros.fiber)}g</span>
                                <span className="macro-label">Fiber</span>
                            </div>
                            <div className="macro-item sodium">
                                <span className="macro-value">{Math.round(context.macros.sodium)}mg</span>
                                <span className="macro-label">Na</span>
                            </div>
                        </div>
                    )}

                    {/* Daily Focus & Health Score Row */}
                    <div className="focus-score-row">
                        <div className="ai-card focus-card">
                            <div className="card-header">
                                <Target className="icon-blue" size={24} />
                                <h3>Daily Focus</h3>
                            </div>
                            <p className="focus-text">
                                {context.daily_focus || "Loading focus..."}
                            </p>
                        </div>

                        <div className="ai-card score-card" style={{ borderColor: getHealthScoreColor(context.health_score_status) }}>
                            <div className="card-header">
                                <HeartPulse size={24} style={{ color: getHealthScoreColor(context.health_score_status) }} />
                                <h3>Health Score</h3>
                                <button
                                    className="sync-button"
                                    onClick={handleManualSync}
                                    disabled={syncLoading || !context.sync_info?.can_sync}
                                    title={context.sync_info ? `${context.sync_info.sync_count}/${context.sync_info.limit} syncs today` : ''}
                                >
                                    <RefreshCw size={16} className={syncLoading ? 'spin' : ''} />
                                </button>
                            </div>
                            <div className="score-content">
                                <div className={`score-badge ${context.health_score_status}`}>
                                    {context.health_score_status?.toUpperCase() || 'N/A'}
                                </div>
                                <p className="score-reason">
                                    {context.health_score_reasoning}
                                </p>
                            </div>
                            {syncError && (
                                <div className="sync-error" style={{ marginTop: '8px', fontSize: '0.8em', color: '#ef4444' }}>
                                    {syncError}
                                </div>
                            )}
                            {syncSuccess && (
                                <div className="sync-success" style={{ marginTop: '8px', fontSize: '0.8em', color: '#22c55e' }}>
                                    ✓ Context refreshed!
                                </div>
                            )}
                            {context.sync_info && (
                                <div className="sync-info" style={{ marginTop: '8px', fontSize: '0.75em', color: '#999', textAlign: 'center' }}>
                                    {context.sync_info.sync_count}/{context.sync_info.limit} manual syncs today
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recommendation & Action Row */}
                    <div className="recommendation-action-row">
                        <div className="ai-card recommendation-card">
                            <div className="card-header">
                                <Lightbulb className="icon-yellow" size={24} />
                                <h3>Next Meal Idea</h3>
                            </div>
                            <p className="recommendation-text">
                                "{context.meal_recommendation}"
                            </p>
                        </div>

                        {context.next_action_suggestion && (
                            <div className="ai-card action-suggestion-card">
                                <div className="card-header">
                                    <ArrowRight className="icon-blue" size={24} />
                                    <h3>Next Step</h3>
                                </div>

                                {actionComplete ? (
                                    <div className="action-confirmation" style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        background: actionComplete === 'done' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                        borderRadius: '8px',
                                        marginTop: '10px'
                                    }}>
                                        <div style={{ fontSize: '2em', marginBottom: '8px' }}>
                                            {actionComplete === 'done' ? '✓' : '👍'}
                                        </div>
                                        <p style={{
                                            margin: 0,
                                            color: actionComplete === 'done' ? '#22c55e' : '#3b82f6',
                                            fontWeight: '600'
                                        }}>
                                            {actionComplete === 'done' ? 'Great job! Task marked complete.' : 'No problem. Here\'s a new suggestion.'}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <p className="action-text">
                                            {context.next_action_suggestion}
                                        </p>
                                        <div className="suggestion-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                                            <button
                                                className="suggestion-btn done"
                                                onClick={() => handleAction('done')}
                                                disabled={actionLoading}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    background: 'var(--success-color, #22c55e)',
                                                    color: 'white',
                                                    fontWeight: '600',
                                                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                                                    opacity: actionLoading ? 0.7 : 1
                                                }}
                                            >{actionLoading ? '...' : 'Done'}</button>
                                            <button
                                                className="suggestion-btn decline"
                                                onClick={() => handleAction('decline')}
                                                disabled={actionLoading}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #ddd',
                                                    background: '#f5f5f5',
                                                    color: '#666',
                                                    fontWeight: '600',
                                                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                                                    opacity: actionLoading ? 0.7 : 1
                                                }}
                                            >Decline</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="ai-card checklist-card">
                        <h3>Daily Checklist</h3>
                        <div className="checklist-items">
                            {context.checklist.map((item) => (
                                <div
                                    key={item.id}
                                    className={`checklist-item ${item.completed ? 'completed' : ''}`}
                                >
                                    {item.completed ? (
                                        <CheckCircle2 className="check-icon" size={20} />
                                    ) : (
                                        <Circle className="circle-icon" size={20} />
                                    )}
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="daily-stats-section">
                <DailySummary
                    refreshTrigger={refreshTrigger}
                    showAiInsights={false}
                    compact={true}
                    disableDateNavigation={true}
                />
            </div>

            <div className="home-actions">
                <h3>Views</h3>
                <div className="action-buttons">
                    <button
                        className="action-card"
                        onClick={() => onNavigate('journal')}
                    >
                        <BookOpen size={32} />
                        <span>Journal</span>
                        <span className="action-desc">Daily summaries</span>
                    </button>

                    <button
                        className="action-card"
                        onClick={() => onNavigate('timeline')}
                    >
                        <Activity size={32} />
                        <span>Timeline</span>
                        <span className="action-desc">View all health events</span>
                    </button>

                    <button
                        className="action-card"
                        onClick={() => onNavigate('stats')}
                    >
                        <BarChart2 size={32} />
                        <span>Statistics</span>
                        <span className="action-desc">Charts and trends</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
